import { Form, Formik, type FormikProps } from "formik";
import React from "react";
import { ManualSave } from "../../../../commons/form-fields/ManualSave.js";

/**
 * Per-card manual save (integrations settings pages): every section card is
 * its own form with its own Save/Cancel row. A card's save must merge only
 * that card's slice over the last saved settings, so other cards' unsaved
 * edits are never swept along. enableReinitialize resets a card once the
 * refetch after save delivers the new stored settings.
 */
export function IntegrationCardForm<T extends object>({
  initialValues,
  validationSchema,
  onSave,
  onDirtyChange,
  children,
}: {
  initialValues: T;
  validationSchema?: unknown;
  onSave: (values: T) => Promise<unknown>;
  onDirtyChange: (dirty: boolean) => void;
  children: (fp: FormikProps<T>) => React.ReactNode;
}) {
  return (
    <Formik<T>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async values => {
        await onSave(values);
      }}
      enableReinitialize
    >
      {fp => (
        <Form>
          <FormDirtyTracker dirty={fp.dirty} onDirtyChange={onDirtyChange} />
          {children(fp)}
        </Form>
      )}
    </Formik>
  );
}

// Right-aligned Save/Cancel inside a card — appears only while that card
// has unsaved changes. `disabled` lets the page block every card's save
// while one save/refetch is in flight: each card's payload is built from
// the render-time stored settings, so a second save racing the first would
// overlay its slice on a stale baseline and restore the first card's old
// values.
export function CardSaveRow<T>({
  fp,
  disabled = false,
}: {
  fp: FormikProps<T>;
  disabled?: boolean;
}) {
  if (!fp.dirty) return null;
  return (
    <ManualSave
      buttonLayout="inline"
      // ManualSave's inline sx uses flex:0 (basis 0%), which collapses the
      // buttons to min-width and wraps their labels — size to content instead
      cancelButtonStyle={{ flex: "0 0 auto" }}
      submitButtonStyle={{ flex: "0 0 auto" }}
      onCancel={fp.handleReset}
      disabled={disabled || fp.isSubmitting}
      disabledSubmit={!fp.isValid}
    />
  );
}

export const FormDirtyTracker = ({
  dirty,
  onDirtyChange,
}: {
  dirty: boolean;
  onDirtyChange: (dirty: boolean) => void;
}) => {
  React.useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);
  return null;
};

/**
 * Per-card dirty registry driving the page's unsaved-changes guard: pass
 * `dirtyOf(cardKey)` as a card form's onDirtyChange (stable per key).
 */
export function useCardDirtyRegistry() {
  const [dirtyCards, setDirtyCards] = React.useState<Record<string, boolean>>(
    {}
  );
  const callbacksRef = React.useRef<Record<string, (d: boolean) => void>>({});
  const dirtyOf = React.useCallback((card: string) => {
    callbacksRef.current[card] ??= (dirty: boolean) =>
      setDirtyCards(prev =>
        prev[card] === dirty ? prev : { ...prev, [card]: dirty }
      );
    return callbacksRef.current[card];
  }, []);
  return { anyDirty: Object.values(dirtyCards).some(Boolean), dirtyOf };
}
